import { useEffect, useState, type ReactElement } from "react";
import { useParams } from "react-router-dom";
import { usePortal, findStage } from "../state/PortalContext.js";
import { PageHelp } from "../layout/PageHelp.js";

interface Program {
  id: string;
  kind: string;
  servingMode: string;
}
interface Run {
  runId: string;
  status: string;
  startedAt?: string;
  endedAt?: string;
  costUsd?: number;
}
interface FairnessGroup {
  facet: string;
  value: string;
  n: number;
  accuracy: number;
  recall: number;
  auc: number;
  dppl: number;
}
interface Version {
  version: string;
  status: string;
  servingMode?: string;
  evaluationPassed: boolean;
  fairnessPassed: boolean;
  metrics?: Record<string, number>;
  synthetic?: boolean;
  fairnessGroups?: FairnessGroup[];
  observedAccuracyGap?: number;
  observedRecallGap?: number;
  observedDisparity?: number;
  scope?: Record<string, unknown>;
}
interface LabelStatus {
  collected: number;
  required: number;
  ready: boolean;
}

/**
 * Models module (DESIGN §13.4): per (service, stage) list programs, runs, versions and metrics, and
 * drive start-run / promote / set-serving. Promotion controls are shown only to admins with
 * {service}:ml:promote; start-run only to {service}:ml:train. The server re-checks every permission
 * and the evaluation + fairness gates, so the UI is a convenience, not the enforcement point.
 */
export function ModelsPage(): ReactElement {
  const { api, catalog } = usePortal();
  const params = useParams();
  const serviceId = params.service!;
  const stageId = params.stage!;
  const stage = findStage(catalog, serviceId, stageId);
  const ml = stage?.ml;

  const [selected, setSelected] = useState<string>("");
  const [runs, setRuns] = useState<Run[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [labels, setLabels] = useState<LabelStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const programs: Program[] = ml?.programs ?? [];
  useEffect(() => {
    if (programs.length > 0 && !selected) setSelected(programs[0].id);
  }, [programs, selected]);

  async function reload(programId: string): Promise<void> {
    setError(null);
    try {
      const r = (await api.mlGet(serviceId, stageId, `programs/${programId}/runs`)) as { runs: Run[] };
      const v = (await api.mlGet(serviceId, stageId, `programs/${programId}/versions`)) as { versions: Version[] };
      setRuns(r.runs ?? []);
      setVersions(v.versions ?? []);
      try {
        const l = (await api.mlGet(serviceId, stageId, `programs/${programId}/labels`)) as Partial<LabelStatus>;
        setLabels(typeof l?.required === "number" ? (l as LabelStatus) : null);
      } catch {
        setLabels(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    if (selected) void reload(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  async function act(action: string, body: Record<string, unknown>): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await api.mlAction(serviceId, stageId, selected, action, body);
      setNotice(`${action} ok`);
      await reload(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : `${action} failed`);
    }
  }

  function confirmBody(extra: Record<string, unknown>): Record<string, unknown> {
    // On a protected stage the server requires the stage id typed to confirm.
    if (stage?.protected) {
      const typed = window.prompt(`Type the stage id "${stageId}" to confirm`);
      return { ...extra, confirmStage: typed };
    }
    return extra;
  }

  if (!ml) return <p role="alert">No ML facility for this stage.</p>;

  return (
    <section aria-labelledby="models-title">
      <h1 id="models-title">Models</h1>
      <PageHelp>
        <p>
          A model learns from reviewers&rsquo; past decisions and gives each new item (for example an application) a
          score. It only ever helps reviewers; it never approves or rejects anything by itself.
        </p>
        <ul>
          <li>
            <strong>Shadow</strong>: the model scores applications but reviewers don&rsquo;t see the score. Use this to check
            it quietly.
          </li>
          <li>
            <strong>Assist</strong>: reviewers with the right role see the score while reviewing.
          </li>
          <li>It only learns from people who agreed to their data being used for training.</li>
        </ul>
      </PageHelp>
      <p>
        Monthly budget: ${ml.budgetMonthlyUsd}{" "}
        <span className="field-hint">(training stops if this month&rsquo;s spend would go over it)</span>
      </p>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      <label htmlFor="program-select">Program</label>
      <p id="program-hint" className="field-hint">
        The model you are looking at, and whether reviewers currently see its scores (serving).
      </p>
      <select id="program-select" value={selected} onChange={(e) => setSelected(e.target.value)} aria-describedby="program-hint">
        {programs.map((p) => (
          <option key={p.id} value={p.id}>
            {p.id} ({p.kind}, serving: {p.servingMode})
          </option>
        ))}
      </select>

      {ml.can.train ? (
        <ul className="action-list">
          <li>
            <button type="button" aria-describedby="run-hint" onClick={() => void act("start-run", { mode: "real" })}>
              Start run
            </button>
            <span id="run-hint" className="field-hint">
              Train a new version on real review decisions. Needs enough decisions first (see below). Costs money.
            </span>
          </li>
          <li>
            <button type="button" aria-describedby="dry-hint" onClick={() => void act("start-run", { mode: "dry-run" })}>
              Start dry run (synthetic)
            </button>
            <span id="dry-hint" className="field-hint">
              Test that training works, using made-up data. The result can never be switched on.
            </span>
          </li>
        </ul>
      ) : null}

      {labels ? (
        <p role="status">
          Real consented labels: <strong>{labels.collected}</strong> of <strong>{labels.required}</strong> required
          {labels.ready ? " (ready for a real run)" : " (dry run only until ready)"}
        </p>
      ) : null}

      <h2>Runs</h2>
      <p className="field-hint">Each time the model was trained, whether it finished, and what it cost.</p>
      <ul>
        {runs.map((r) => (
          <li key={r.runId}>
            {r.runId}: {r.status}
            {r.costUsd != null ? ` ($${r.costUsd})` : ""}
          </li>
        ))}
      </ul>

      <h2>Model versions</h2>
      <p className="field-hint">
        Every trained version. Eval = was it accurate enough. Fairness = does it treat groups (e.g. languages) about the
        same. A version can only be switched to Assist if both say pass. Rollback stops using a version.
      </p>
      <table>
        <thead>
          <tr>
            <th>Version</th>
            <th>Status</th>
            <th>Serving</th>
            <th>Eval</th>
            <th>Fairness</th>
            {ml.can.promote ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.version}>
              <td>{v.version}</td>
              <td>{v.synthetic ? "Dry run (synthetic)" : v.status}</td>
              <td>{v.servingMode ?? "none"}</td>
              <td>{v.evaluationPassed ? "pass" : "fail"}</td>
              <td>{v.fairnessPassed ? "pass" : "fail"}</td>
              {ml.can.promote ? (
                <td>
                  {v.synthetic ? (
                    <span>Not promotable</span>
                  ) : (
                    <>
                      <button
                        type="button"
                        aria-label={`Promote ${v.version} to assist`}
                        onClick={() => void act("promote", confirmBody({ version: v.version, mode: "assist" }))}
                      >
                        Promote to assist
                      </button>
                      <button
                        type="button"
                        aria-label={`Rollback ${v.version}`}
                        onClick={() => void act("rollback", confirmBody({ version: v.version }))}
                      >
                        Rollback
                      </button>
                    </>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Per-group fairness (holdout)</h2>
      <p className="field-hint">
        How well each version does for each group, measured on applications it did not learn from. n = number of
        applications; Accuracy = share it got right; Recall = share of good applicants it recognised; AUC = overall
        ranking quality (1 is perfect, 0.5 is guessing); DPPL = difference in how often groups get a positive score.
        Big differences between groups are a warning sign.
      </p>
      {versions.filter((v) => v.fairnessGroups && v.fairnessGroups.length > 0).length === 0 ? (
        <p>No per-group fairness metrics recorded yet.</p>
      ) : (
        versions
          .filter((v) => v.fairnessGroups && v.fairnessGroups.length > 0)
          .map((v) => (
            <table key={`fair-${v.version}`}>
              <caption>
                Fairness for {v.version} — accuracy gap {fmtGap(v.observedAccuracyGap)}, recall gap{" "}
                {fmtGap(v.observedRecallGap)}, disparity {fmtGap(v.observedDisparity)} ({v.fairnessPassed ? "pass" : "fail"})
              </caption>
              <thead>
                <tr>
                  <th scope="col">Facet</th>
                  <th scope="col">Group</th>
                  <th scope="col">n</th>
                  <th scope="col">Accuracy</th>
                  <th scope="col">Recall (TPR)</th>
                  <th scope="col">AUC</th>
                  <th scope="col">DPPL</th>
                </tr>
              </thead>
              <tbody>
                {v.fairnessGroups!.map((g) => (
                  <tr key={`${v.version}-${g.facet}-${g.value}`}>
                    <th scope="row">{g.facet}</th>
                    <td>{g.value}</td>
                    <td>{g.n}</td>
                    <td>{g.accuracy}</td>
                    <td>{g.recall}</td>
                    <td>{g.auc}</td>
                    <td>{g.dppl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))
      )}
    </section>
  );
}

function fmtGap(v?: number): string {
  return typeof v === "number" ? v.toFixed(3) : "n/a";
}

export default ModelsPage;
